import {
  getWeek,
  getDay,
  setDate,
  getDate,
  endOfYear,
  differenceInCalendarDays,
  startOfMonth,
  getMonth,
  nextMonday,
  isSaturday,
  isSunday,
  nextFriday,
  endOfMonth,
  addDays,
  isWeekend,
  addWeeks,
  addMonths,
  addYears,
  isFriday
} from 'date-fns';

/**
 * Calculates the next working day based on the given date.
 * If the given date is a Friday, it returns the following Monday.
 * If the given date is any other day, it returns the next day.
 * @param {Date} [date=new Date()] - The date for which to calculate the next working day.
 * @returns {Date} The next working day.
 */
  export const getNextWorkingDay = (date = new Date()) => {
    const nextWorkingDay = addDays(date, isFriday(date) ? 3 : 1);
    return nextWorkingDay;
  };

/**
 * @description Get trimester index from a fiven month
 * @param {number} monthIndex between 0 and 11
 * @returns {number} between 1 and 4
 */
export const getTrimester = (monthIndex) => {
  if (monthIndex < 3) return 1;
  if (monthIndex < 6) return 2;
  if (monthIndex < 9) return 3;
  return 4;
};

/**
 * @description Get trimester array from a fiven month
 * @param {number} monthIndex between 0 and 11
 * @returns {Array.<number>} between 1 and 4
 */
export const getTrimesterMonths = (monthIndex) => {
  const trimester = getTrimester(monthIndex);
  const firstTrimesterMonth = (trimester - 1) * 3;
  return [firstTrimesterMonth, firstTrimesterMonth + 1, firstTrimesterMonth + 2];
};

/**
 * @description Return ISO week number for given date
 * @param {Date} date
 * @returns {number} between 1 and 53
 */
export const getISOWeek = (date) => getWeek(date, { weekStartsOn: 1, firstWeekContainsDate: 4 });

/**
 * @description Return number of ISO week number for given year
 * @param {number} year
 * @returns {number} 52 or 53
 */
export const getNumberOfISOWeekInYear = (year) => getISOWeek(endOfYear(new Date(year, 0, 1)));

/**
 * @description Return date for start of ISO week in given year
 * @param {number} week
 * @param {number} year
 * @returns {Date} date of Monday at start of week
 */
export const getFirstDateOfISOWeek = (week, year) => {
  // Get date for 1 Jan in given year
  let date = new Date(year, 0, 1);
  // Shift to start of ISO week 1
  date = setDate(date, (getDay(date) <= 4 ? 2 : 9) - getDay(date));
  // Add required number of weeks
  date = setDate(date, getDate(date) + (week - 1) * 7);
  return date;
};

/**
 * @description Return date for a day in given ISO week in given year
 * @param {number} day Mon=1, Tue=2, ... Sun=0 or 7
 * @param {number} week
 * @param {number} year
 * @returns {Date} date of required day
 */
export const getDateOfISOWeek = (day, week, year) => {
  let date = getFirstDateOfISOWeek(week, year);
  date = setDate(date, getDate(date) + (+day || 7) - 1);
  return date;
};

/**
 * @description Return trimester end year
 * @param startMonth
 * @param startYear
 * @returns {number}
 */
export const getTrimesterEndYear = (startMonth, startYear) => (
  startMonth > 9 ? startYear + 1 : startYear
);

/**
 * @description Return trimester end month
 * @param startMonth
 * @returns {number}
 */
export const getTrimesterEndMonth = (startMonth) => (
  startMonth > 9 ? startMonth - 10 : startMonth + 2
);

/**
 * @description Return number of weeks in a month
 * @param year
 * @param month
 * @returns {Array}
 */
export const getWeeksInMonth = (year, month, workingDaysOnly = false) => {
  const weeks = [];
  const startDate = startOfMonth(new Date(year, month));

  let currDate = startDate;
  while (getMonth(currDate) === getMonth(startDate)) {
    const day = getDay(currDate) === 0 ? 6 : getDay(currDate) - 1;
    const nbDays = 7 - day;
    const allDays = [];
    for (let i = 0; i < nbDays; i += 1) {
      const newDay = addDays(currDate, i);
      if (getMonth(newDay) === getMonth(currDate)) allDays.push(newDay);
    }
    const workingDays = allDays.filter((d) => !isSaturday(d) && !isSunday(d));

    weeks.push({
      number: getISOWeek(currDate),
      days: workingDaysOnly ? workingDays : allDays,
    });
    currDate = nextMonday(currDate);
  }

  return weeks;
};

// SlotId stands for the number of half days since 01/01/2015.
// The 01/01/2015 has id 1 and the afternoon as id 2 etc ...
/**
 * @description Converts slotId into normal Date
 * @param slotId
 * @returns {Date}
 */
export const slotIdToDate = (slotId) => {
  const daysToAdd = Math.round(slotId / 2);
  return addDays(new Date(2015, 0, 1), daysToAdd - 1);
};

/**
 * @description Converts date to slotId
 * @param date
 * @returns {number}
 */
export const dateToSlotId = (date) => {
  const genesis = new Date(2015, 0, 1);
  return (differenceInCalendarDays(date, genesis) + 1) * 2 - 1;
};

/**
 * @description Return start and end dates for scheduler view
 * @param viewType
 * @param month
 * @param week
 * @param trimester
 * @param year
 * @returns {Dates}
 */
export const getLimitDates = (viewType, month, week, trimester, year) => {
  // month view
  let startDate = startOfMonth(new Date(year, month));
  let endDate = endOfMonth(new Date(year, month));
  // week view
  if (viewType === 'week') {
    startDate = getFirstDateOfISOWeek(week, year);
    endDate = nextFriday(getFirstDateOfISOWeek(week, year));
  }
  // trimester view
  if (viewType === 'trimester') {
    startDate = startOfMonth(new Date(year, trimester));
    endDate = endOfMonth(
      new Date(getTrimesterEndYear(trimester, year), getTrimesterEndMonth(trimester)),
    );
  }
  return { startDate, endDate };
};

/**
 * @description Converts limit dates to array of slotIds
 * @param startDate
 * @param endDate
 * @param period : "AM" | "PM" | "FULL" | "CUSTOM"
 * @param customPeriod
 * @param recurrence
 * @returns {Array<number>}
 */
export const getSlotIdsBetweenTwoDates = (
  startDate,
  endDate,
  period,
  customPeriod = null,
  recurrence = null,
) => {
  const firstSlotId = dateToSlotId(startDate);
  const lastSlotId = dateToSlotId(endDate) + 1;
  const slotIds = period !== 'CUSTOM'
    ? Array.from(
      { length: (lastSlotId - firstSlotId) / (period === 'FULL' ? 1 : 2) + 1 },
      (_, i) => firstSlotId + (period === 'FULL' ? 1 : 2) * i + (period === 'PM' ? 1 : 0),
    )
    : [];

  if (period === 'CUSTOM') {
    customPeriod.forEach((w) => w.forEach((d) => {
      if (d.values?.am) slotIds.push(dateToSlotId(d.date));
      if (d.values?.pm) slotIds.push(dateToSlotId(d.date) + 1);
    }));
  }

  if (recurrence) {
    const slotIdsBuffer = [...slotIds];
    Array.from({ length: recurrence.number - 1 }, (_, i) => i + 1).forEach((i) => {
      slotIdsBuffer.forEach((s) => {
        const isAfternoon = s % 2 === 0;
        let newDate = slotIdToDate(s);
        if (recurrence.period.value === 'WEEK') newDate = addWeeks(newDate, i * recurrence.interval);
        if (recurrence.period.value === 'MONTH') newDate = addMonths(newDate, i * recurrence.interval);
        if (recurrence.period.value === 'YEAR') newDate = addYears(newDate, i * recurrence.interval);
        const newSlotId = dateToSlotId(newDate) + (isAfternoon ? 1 : 0);
        slotIds.push(newSlotId);
      });
    });
  }

  return slotIds.filter((slotId) => !isWeekend(slotIdToDate(slotId)));
};
